import { Card, Heading, Icon, Layout, Stack } from "@shopify/polaris";
import React from "react";
import styles from "./ArtifactGallery.module.css";
import { CircleTickMajor } from "@shopify/polaris-icons";
import InspectionElement from "../../types/InspectionElement";
import { ArtifactGrid } from "../ArtifactGrid";
import { Tick } from "../Indicators";
import { AssetType } from "../../types/AssetType";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
    addImageToDeleteList,
    removeImageFromDeleteList
} from "../../store/actions/Inspections";
import DeleteBulkModal from "../DeleteView/DeleteBulkModal";
import { ImageDeleteData } from "../../types/ImageDeleteData";
import SurveyorCompletionStatus from "../../types/SurveyorCompletionStatus";
import {PortalState} from "../../types/PortalState";

interface Props {
    data: InspectionElement;
}

export default function ArtifactGallery({ data }: Props) {
    const dispatch = useDispatch();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const localUpdateTime = useSelector(
        (state: any) => state.portal.activeInspection?.localUpdateTime
    );
    const imagesDeleteList: ImageDeleteData[] = useSelector(
        (state: any) => state.portal.activeInspection.ImageDeleteList
    );

    const surveyorCompletionStatus: SurveyorCompletionStatus | undefined =
        useSelector(
            (state: PortalState) =>
                state.portal.activeInspection?.SurveyorCompletionStatus
        );


    const [showDeleteModal, setShowDeleteModal] = React.useState(false);

    const { t } = useTranslation("common");

    const getGalleryTitle = () => {
        if (data.ElementType === AssetType.Image) {
            return "Photos";
        }

        if (data.ElementType === AssetType.Document) {
            return null;
        }
    };

    const getHeaderForDocuments = () => {
        if (data.ElementType !== AssetType.Document) {
            return null;
        }

        if (data.Documents.length >= data.MinItems && data.MinItems != null) {
            return (
                <div className={styles.documentCardHeaderValid}>
                    <p>{t("inspection.documents.valid")}</p>
                </div>
            );
        }

        return (
            <div className={styles.documentCardHeaderRequired}>
                <p>{t("inspection.documents.required")}</p>
            </div>
        );
    };

    const setCheckboxes = (forDelete: boolean) => {
        const deleteCheckboxes = document.querySelectorAll(
            '[name="deleteCheckbox"]'
        );
        deleteCheckboxes.forEach((checkbox) => {
            if (forDelete) {
                dispatch(
                    addImageToDeleteList({
                        elementId: checkbox.getAttribute("data-element-id"),
                        assetId: checkbox.getAttribute("data-asset-id")
                    })
                );
            } else {
                dispatch(
                    removeImageFromDeleteList({
                        elementId: checkbox.getAttribute("data-element-id"),
                        assetId: checkbox.getAttribute("data-asset-id")
                    })
                );
            }
        });
    };

    const handleSelectAllForDelete = (onClickEvent: any) => {
        const selectBox = onClickEvent.target as HTMLInputElement;
        setCheckboxes(selectBox.checked);

        onClickEvent.stopPropagation();
    };

    const areAllCheckboxesSelected = () => {

        const numberOfDeleteCheckboxes = document.querySelectorAll(
            '[name="deleteCheckbox"]'
        ).length;

        const numberOfSelectedCheckboxes = imagesDeleteList ? imagesDeleteList.length : 0;

        if (numberOfSelectedCheckboxes === 0) {
            return false;
        }

        return numberOfSelectedCheckboxes === numberOfDeleteCheckboxes;
    };


    const handleDeleteSelected = (onClickEvent: any) => {
        if (allowDelete) setShowDeleteModal(true);

        onClickEvent.stopPropagation();
    };

    const handleSelectAllClick = (onClickEvent: any) => {
        onClickEvent.stopPropagation();
    };

    const assetType = data.ElementType;

    const allowDelete: boolean =
        imagesDeleteList !== undefined ? imagesDeleteList.length !== 0 : false;

    function maxItemDisplay() {
        if (data.ElementType === AssetType.Document) {
            return null;
        }

        const deleteElements = () => {
            if(surveyorCompletionStatus?.PhotosComplete){
                return null
            }

            return <>
            <input
                type="checkbox"
                onChange={handleSelectAllForDelete}
                onClick={handleSelectAllClick}
                checked={areAllCheckboxesSelected()}
            />
            {
                " "
            }
            <p id={styles.selectAll}>
                {t("inspection.photos.select_all")}

            </p>
            |
            <img
                alt="delete selected images"
                src={
                    allowDelete
                        ? "/images/trash-red.svg"
                        : "/images/trash-grey.svg"
                }
                id={
                    allowDelete
                        ? styles.deleteSelectedIcon
                        : styles.deleteSelectedIconDisabled
                }
                onClick={handleDeleteSelected}
            />
            {
                " "
            }
            <p
                id={
                    allowDelete
                        ? styles.deleteSelected
                        : styles.deleteSelectedDisabled
                }
                onClick={handleDeleteSelected}
            >
                <span className={styles.deleteSelected}>
                {t("inspection.photos.delete_selected")}
                </span>
                |
            </p>
            </>
        }

        return (
            <div className={styles.smallText}>
                <div>
                    <div className={styles.completed}>
                        {deleteElements()}
                        {data.Images.length} Photos Min {data.MinItems}, Max{" "}
                        {data.MaxItems}
                        <div className={styles.tick}>
                            <Tick element={data}/>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.mobilePadding}>
            <DeleteBulkModal
                active={showDeleteModal}
                closeModal={() => {
                    setShowDeleteModal(false);
                }}
            />
            <Layout>
                <Layout.Section>
                    <Card>
                        {getHeaderForDocuments()}
                        <div className={styles.padding18}>
                            <Stack
                                distribution="equalSpacing"
                                alignment="trailing"
                            >
                                <Stack>
                                    <div>
                                        <Heading>{getGalleryTitle()}</Heading>
                                    </div>
                                </Stack>
                                <Stack>
                                    {maxItemDisplay()}
                                    {data.Completed ? (
                                        <Icon
                                            color="success"
                                            source={CircleTickMajor}
                                        />
                                    ) : null}
                                </Stack>
                            </Stack>
                            <ArtifactGrid
                                assetType={assetType}
                                elementId={data.Id}
                                assetSize="large"
                            />
                        </div>
                    </Card>
                </Layout.Section>
            </Layout>
        </div>
    );
}
